import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosRequestConfig,
} from "axios";
import { env, TIMEOUTS } from "@monorepo/config";
import {
  mapApiError,
  createNetworkError,
  createTimeoutError,
  isRetryable,
  logError,
  type ApiError,
  type NetworkError,
} from "./errors";
import { getTemporaryData } from "./token-manager";

/**
 * CSRF Token Storage
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string) {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function clearCsrfToken() {
  csrfToken = null;
}

/**
 * Create Axios instance
 */
function createAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: TIMEOUTS.REQUEST_TIMEOUT_MS,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });

  instance.interceptors.request.use(
    (config) => handleRequest(config),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => handleResponse(response),
    (error) => handleResponseError(error)
  );

  return instance;
}

function handleRequest(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  const method = config.method?.toUpperCase() || "";
  if (csrfToken && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    config.headers["X-Frappe-CSRF-Token"] = csrfToken;
  }

  // Gateway Requirement: X-Userinfo (Base64 encoded JSON)
  const email = getTemporaryData<string>("email");
  if (email && !config.headers["X-Userinfo"]) {
    try {
      // Gateways often expect X-Userinfo to be a Base64-encoded JSON object
      config.headers["X-Userinfo"] = btoa(JSON.stringify({ email }));
    } catch (e) {
      // Fallback to raw email if btoa fails (e.g. non-ASCII, though rare in emails)
      config.headers["X-Userinfo"] = email;
    }
  }

  if (env.isDevelopment) {
    console.info(`[HTTP] ${method} ${config.url}`, config.data || "");
  }

  return config;
}

function handleResponse(response: AxiosResponse): AxiosResponse {
  const newCsrfToken =
    response.headers["x-csrf-token"] ||
    response.headers["x-xsrf-token"] ||
    response.headers["x-frappe-csrf-token"];

  if (newCsrfToken) {
    setCsrfToken(newCsrfToken);
  }

  // SECURITY: Check for application-level errors even in 200 OK responses.
  // Some backend frameworks (like Frappe) might return error details in a 200 response body.
  if (response.data && typeof response.data === "object" && response.data.error) {
    // We throw to trigger handleResponseError which will normalize and log it correctly.
    throw { response } as AxiosError;
  }

  if (env.isDevelopment) {
    console.info(`[HTTP] Response: ${response.status}`, response.data);
  }

  return response;
}

/**
 * Response Error Interceptor
 * Handles errors and retry logic
 */
async function handleResponseError(error: AxiosError): Promise<never> {
  const config = error.config as InternalAxiosRequestConfig & {
    _retryCount?: number;
  };

  let parsedError: ApiError | NetworkError;

  // 1. Normalize the Error
  // We do NOT return early here anymore. We assign to parsedError and flow down to Retry Logic.
  if (!error.response) {
    // Network or Timeout Error
    if (
      error.code === "ECONNABORTED" ||
      error.message.toLowerCase().includes("timeout")
    ) {
      parsedError = createTimeoutError();
      logError(parsedError, "HTTP Request Timeout");
    } else {
      parsedError = createNetworkError(
        "Unable to connect to server. Please check your internet connection.",
        error
      );
      logError(parsedError, "HTTP Network Error");
    }
  } else {
    // API Response Error
    parsedError = mapApiError(error.response.data, error.response.status);

    if (parsedError.status !== 401) {
      logError(parsedError, "HTTP Request Failed");
    }

    if (error.response.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    if (error.response.status === 403) {
      if (
        parsedError.code === "CSRF_TOKEN_INVALID" ||
        parsedError.code === "INVALID_CSRF_TOKEN"
      ) {
        clearCsrfToken();
      }
    }
  }

  // 2. Retry Logic
  // FIX: Explicitly exclude 500 (Internal Server Error) from retries.
  const isServerBug = "status" in parsedError && parsedError.status === 500;

  // FIX: Check isRetryable on the parsedError (works for both ApiError and NetworkError now)
  if (config && isRetryable(parsedError) && !isServerBug) {
    const retryCount = config._retryCount || 0;

    if (retryCount < TIMEOUTS.MAX_RETRIES) {
      config._retryCount = retryCount + 1;

      // Exponential backoff
      const delay = TIMEOUTS.RETRY_DELAY_MS * Math.pow(2, retryCount);

      await new Promise((resolve) => setTimeout(resolve, delay));

      // Retry using the singleton instance
      return httpClient.request(config);
    }
  }

  return Promise.reject(parsedError);
}

export const httpClient: AxiosInstance = createAxiosInstance();

export async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.get<T>(url, config);
  return response.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.post<T>(url, data, config);
  return response.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.put<T>(url, data, config);
  return response.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.patch<T>(url, data, config);
  return response.data;
}

export async function del<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.delete<T>(url, config);
  return response.data;
}

export async function checkHealth(): Promise<boolean> {
  try {
    await httpClient.get("/health", { timeout: 5000 });
    return true;
  } catch {
    return false;
  }
}
